import React, { useEffect, useState } from 'react';
import { Button, Chip, TextArea, Typography } from '@navi/web-ui/lib/primitives';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '@cp/src/components/DateTimePicker/constants';
import { DateFormat, getFormatDate, getFormattedDateWithFullYear } from '@cp/src/utils/DateHelper';
import { defaultValues, PAUSE_MESSAGE, PAUSE_REASON_TYPES, PauseFormValues } from './const';
import InfoFilledIcon from '@cp/src/assets/icons/InfoFilledIcon';
import { getPauseDate, submitPauseAction } from './action';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { createKey } from '@cp/src/utils/CaseDetail.utils';
import { logError } from '@cp/src/utils/ApiHelper';
import PauseIcon from '@cp/src/assets/icons/PauseIcon';
import styles from './index.module.scss';
import cx from 'classnames';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { getDateAfterCertainMonths } from '@cp/src/utils/commonUtils';

const PauseAction = () => {
  const todayDate = getFormatDate(new Date(), DateFormat.YYYY_MM_DD);
  const threeMonthsLaterDate = getFormatDate(
    getDateAfterCertainMonths(new Date(), 12),
    DateFormat.YYYY_MM_DD
  );

  const [isSelectedActionUpdatePause, setIsSelectedActionUpdatePause] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loanId = '', customerId = '', caseBusinessVertical = '' } = useParams();
  const pageDetail = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );

  const caseSnoozeDate = pageDetail?.pause?.data?.pauseTillDate || '';
  const isSnoozed = pageDetail?.pause?.data?.isPaused || '';

  const dispatch = useDispatch();
  const caseReferenceId = pageDetail?.details?.data?.caseReferenceId || '';

  const handleSubmitState = (state: boolean) => {
    setIsSubmitting(state);
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    control
  } = useForm<PauseFormValues>({
    defaultValues
  });

  const fetchPauseStatus = async () => {
    try {
      dispatch(getPauseDate(loanId, customerId, caseReferenceId, caseBusinessVertical));
    } catch (error) {
      logError(error, 'Error while fetching pause status');
    }
  };

  useEffect(() => {
    if (!caseReferenceId) return;
    fetchPauseStatus();
  }, [caseReferenceId]);

  const handleSubmitForm = (data: PauseFormValues) => {
    const { pauseDate, reason } = data;
    const payload = {
      pauseEffortsTillDate: pauseDate,
      pauseEffortsReason: reason === 'OTHERS' ? data.details : reason
    };

    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_SNOOZE_EFFORTS_SUBMITTED, {
      caseId: caseReferenceId,
      reason: payload.pauseEffortsReason,
      pauseEffortsTillDate: pauseDate,
      loanId: loanId
    });

    submitPauseAction({
      caseId: caseReferenceId,
      payload: payload,
      setLoading: handleSubmitState,
      successCallback: fetchPauseStatus
    });
    reset(defaultValues);
  };

  const isFormDisabled =
    !caseReferenceId.length ||
    isSubmitting ||
    !watch('pauseDate') ||
    !watch('reason') ||
    (watch('reason') === 'OTHERS' && !watch('details'));

  return (
    <div className={styles.container} key={String(isSnoozed)}>
      <div className={styles.feedback}>
        <div className={'mt-6 mb-4'}>
          <Typography variant="h3">
            {isSnoozed ? PAUSE_MESSAGE.UPDATE_PAUSE : PAUSE_MESSAGE.PAUSE_COLLECTIONS}
          </Typography>
        </div>
        <Controller
          control={control}
          name="pauseDate"
          rules={{
            validate: value =>
              !(isSelectedActionUpdatePause && value === caseSnoozeDate) || PAUSE_MESSAGE.DATE_ERROR
          }}
          render={({ field: { onChange, value } }) => (
            <DateTimePickerComponent
              key={isSubmitting as unknown as string}
              type={DATE_TIME_TYPE.DATE}
              min={todayDate}
              max={threeMonthsLaterDate}
              value={value}
              className={'mt-4'}
              containerClasses={styles.pauseDatePicker}
              inputClasses={styles.pauseDatePickerInput}
              onChange={onChange}
              required
              heading={
                isSelectedActionUpdatePause
                  ? PAUSE_MESSAGE.UPDATE_PAUSE_DAYS
                  : PAUSE_MESSAGE.PAUSE_TILL
              }
              footer={isSelectedActionUpdatePause ? '' : PAUSE_MESSAGE.PAUSE_IMMEDIATE_TODAY}
            />
          )}
        />

        {errors?.pauseDate && (
          <Typography variant="p4" color="var(--navi-color-red-base)">
            {errors?.pauseDate?.message}
          </Typography>
        )}

        <div className={'mt-4 mb-1'}>
          <Typography as="span" variant="p4" color="var(--navi-color-gray-c2)">
            {isSelectedActionUpdatePause ? PAUSE_MESSAGE.REASON_UPDATE : PAUSE_MESSAGE.REASON_PAUSE}
          </Typography>
        </div>

        <Controller
          control={control}
          name="reason"
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <div className={styles.questions}>
              {PAUSE_REASON_TYPES.map(pauseType => (
                <Chip
                  key={pauseType.value}
                  label={pauseType.label}
                  wrapperClasses={cx([
                    styles.chip,
                    { [styles.chipActiveColor]: pauseType.value === value }
                  ])}
                  labelClasses={styles.text}
                  onChipClick={() => onChange(pauseType.value)}
                />
              ))}
            </div>
          )}
        />

        {watch('reason') === 'OTHERS' ? (
          <TextArea
            {...register('details', { required: true })}
            placeholder=""
            id="comments"
            resize
            fullWidth
            required
            label="Other reasons"
            rows={4}
            containerClassName={styles.textAreaContainer}
            className={cx(styles.textAreaContainer, 'mt-4')}
          />
        ) : null}
      </div>

      <div className={cx(styles.submitButtonSection, 'p-4')}>
        <Button
          fullWidth
          type={'submit'}
          onClick={handleSubmit(handleSubmitForm)}
          className="bg-[var(--navi-color-red-base)]"
          disabled={isFormDisabled}
        >
          {isSelectedActionUpdatePause
            ? PAUSE_MESSAGE.UPDATE_PAUSE_DATE
            : PAUSE_MESSAGE.PAUSE_COLLECTIONS_EFFORTS}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(PauseAction);
